/**
 * Plan equity views at a date (Plan.totalPropertyEquity, assetEquity, propertyEquity,
 * depreciatingAssetEquity, calculateDebtBalance). A sale removes the item's equity from the
 * start of the sale month.
 */
import { monthsBetween, type Day } from '../core/calendar.ts';
import { abs, gt, max, sub, sum, ZERO, type Decimal } from '../core/decimal.ts';
import { assetValue } from '../models/asset.ts';
import { appreciatedValue } from './appreciation.ts';
import { depreciatedValueAfter, remainingEmbeddedFinance } from '../models/depreciating-asset.ts';
import { calculateMortgageBalance } from '../models/mortgage.ts';
import { isOwnedOn, isSoldBy, linkedDebts } from '../models/plan.ts';
import type { Asset, CalcContext, DepreciatingAsset, Id, Plan, Property, Transaction } from '../types/domain.ts';
import { remainingBalance } from './debt-projector.ts';

/** Plan.calculateDebtBalance: DebtProjector.calculateRemainingBalance. */
export const debtBalanceAt = (debt: Transaction, date: Day, ctx: CalcContext): Decimal =>
  remainingBalance(debt, date, ctx.now);

const NONE: ReadonlySet<Id> = new Set();

/** Σ |balance| of the asset's linked debts, leaving out `excluding` (debts counted in the debt line instead). */
const outstandingOn = (plan: Plan, assetId: Id, date: Day, ctx: CalcContext, excluding: ReadonlySet<Id>): Decimal =>
  sum(
    linkedDebts(plan, assetId)
      .filter((debt) => !excluding.has(debt.id))
      .map((debt) => abs(debtBalanceAt(debt, date, ctx))),
  );

/** Plan.assetEquity: value − Σ |linked debt balance| (except `excludingDebts`); 0 before the purchase (R23) and once sold. */
export function assetEquity(plan: Plan, asset: Asset, date: Day, ctx: CalcContext, excludingDebts = NONE): Decimal {
  if (!isOwnedOn(asset, date)) return ZERO;
  return sub(assetValue(asset, date), outstandingOn(plan, asset.id, date, ctx, excludingDebts));
}

/**
 * Plan.propertyEquity (legacy): appreciated value (R5) less linked debts (except
 * `excludingDebts`) when there are any, else less the embedded mortgage's amortised balance;
 * 0 once sold.
 */
export function propertyEquity(plan: Plan, property: Property, date: Day, ctx: CalcContext, excludingDebts = NONE): Decimal {
  if (date < property.startDate || isSoldBy(property.saleEvent, date)) return ZERO;
  const value = appreciatedValue(property.propertyValue, property.annualAppreciationRate, property.startDate, date);
  if (linkedDebts(plan, property.id).length > 0) {
    return sub(value, outstandingOn(plan, property.id, date, ctx, excludingDebts));
  }
  if (!gt(property.mortgage.loanAmount, ZERO)) return value;
  return sub(value, calculateMortgageBalance(property.mortgage, date));
}

/**
 * Plan.depreciatingAssetEquity (legacy): fractional-year value (D2b) less linked debt balances
 * summed without abs (D14), or the embedded finance when nothing is linked; floored at 0.
 */
export function depreciatingAssetEquity(plan: Plan, asset: DepreciatingAsset, date: Day, ctx: CalcContext): Decimal {
  if (!isOwnedOn(asset, date)) return ZERO;
  const monthsOwned = monthsBetween(asset.purchaseDate, date);
  const debts = linkedDebts(plan, asset.id);
  const remainingDebt =
    debts.length === 0
      ? remainingEmbeddedFinance(asset, monthsOwned)
      : sum(debts.map((debt) => debtBalanceAt(debt, date, ctx)));
  return max(ZERO, sub(depreciatedValueAfter(asset, monthsOwned), remainingDebt));
}

/**
 * Plan.totalPropertyEquity: active unified property assets, then active legacy properties not
 * in `assets`; `excludingDebts` are mortgages treated as debt, so not netted here (R3).
 */
export function totalPropertyEquity(plan: Plan, date: Day, ctx: CalcContext, excludingDebts = NONE): Decimal {
  const unifiedIds = new Set(plan.assets.map((a) => a.id));
  const unified = plan.assets
    .filter((a) => a.assetClass === 'property' && a.isActive)
    .map((a) => assetEquity(plan, a, date, ctx, excludingDebts));
  const legacy = plan.properties
    .filter((p) => p.isActive && !unifiedIds.has(p.id))
    .map((p) => propertyEquity(plan, p, date, ctx, excludingDebts));
  return sum([...unified, ...legacy]);
}
